import React, { FC } from 'react';

import styled from 'styled-components';

import { Flex } from '@adsk/alloy-react-box';
import Button from '@adsk/alloy-react-button';
import AlloyFilterButton from '@adsk/alloy-react-filter-button';
import { PlusIcon } from '@adsk/alloy-react-icon';
import SearchField from '@adsk/alloy-react-search-field';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';

import { useTranslation } from '../i18n';
import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';
import { CreateFormProps, PluginQueryFilters } from '../types/types';

const StyledAlloyFilterButton = styled(AlloyFilterButton)({
  marginLeft: theme.spacing.S,
});

interface EntityPickerModalTopBarProps {
  showSearchBox: boolean;
  searchExpression?: string;
  onSearchChange: (searchExpression: string) => void;
  canCreate: boolean;
  onCreateClick: () => void;
  plugin?: CommonReferencePickerPlugin | null;
  disabled?: boolean;
  filtersAvailable?: boolean;
  filtersOpen: boolean;
  filters: PluginQueryFilters;
  setFilters?: (filters: PluginQueryFilters) => void;
  toggleFiltersPanel: () => void;
  clearFilters: () => void;
  filtersCount: number;
  infoMessage?: React.ReactNode;
  createFormProps?: CreateFormProps;
  style?: React.CSSProperties;
  isPluginDisabledInEntitlements?: boolean;
}

const EntityPickerModalTopBar: FC<EntityPickerModalTopBarProps> = ({
  searchExpression,
  onSearchChange,
  showSearchBox,
  canCreate,
  onCreateClick,
  plugin,
  disabled,
  filtersAvailable,
  filtersOpen,
  filters,
  setFilters,
  toggleFiltersPanel,
  clearFilters,
  filtersCount,
  infoMessage,
  createFormProps,
  style = {},
}) => {
  const { t } = useTranslation();
  const {
    CreateNewButtonText,
    renderCreateNewButtonIcon,
    TopBarActions,
    CustomCreateNewButton,
    CustomPickerSearchBox,
    CustomFilterButton,
  } = plugin ?? {};

  const FilterButton = CustomFilterButton || StyledAlloyFilterButton;

  return (
    <Flex justifyContent="space-between" marginBottom="16px" style={style}>
      <Flex alignItems="center">
        {canCreate &&
          (CustomCreateNewButton ? (
            <CustomCreateNewButton
              onCreateClick={onCreateClick}
              disabled={!!disabled}
              createFormProps={createFormProps}
            />
          ) : (
            <Button
              onClick={onCreateClick}
              variant={Button.VARIANTS.SECONDARY}
              renderIcon={renderCreateNewButtonIcon || PlusIcon.renderIcon}
              disabled={disabled}
              data-testid="EntityPickerModalTopBar__create-button"
            >
              {CreateNewButtonText ? <CreateNewButtonText /> : t('CREATE')}
            </Button>
          ))}
        {infoMessage}
      </Flex>
      <Flex alignItems="center">
        {showSearchBox &&
          (CustomPickerSearchBox ? (
            <CustomPickerSearchBox
              disabled={!!disabled}
              searchExpression={searchExpression}
              onSearchChange={onSearchChange}
            />
          ) : (
            <SearchField
              style={{
                width: 300,
                'input:disabled': {
                  background: theme.colors.white,
                },
              }}
              placeholder={t('SEARCH')?.toString()}
              value={searchExpression || ''}
              onChange={onSearchChange}
              disabled={disabled}
            />
          ))}
        {filtersAvailable && (
          <FilterButton
            filtersOpen={filtersOpen}
            filters={filters}
            onClick={toggleFiltersPanel}
            clearFilters={clearFilters}
            filtersCount={filtersCount}
            disabled={!!disabled}
            data-testid="EntityPickerModalTopBar__filter-button"
            {...(CustomFilterButton && {
              setFilters,
            })}
          />
        )}
        {TopBarActions && <TopBarActions />}
      </Flex>
    </Flex>
  );
};

export default WithAlloySuspense(EntityPickerModalTopBar);
